/**
 * Inventory & crafting screen — redesigned per the UX audit:
 * tabs (Criação / Inventário / Personagem), click-first recipe cards
 * with a detail panel (bottom-sheet on narrow screens), plain-language
 * blocked states, "craft all", X button + backdrop tap + ESC to close.
 */

import type { ClassDef, Recipe } from "@craftgaming/shared";
import type { Inventory } from "@craftgaming/shared";
import { RECIPES, canCraft, isFood } from "@craftgaming/shared";
import { BLOCK_NAMES, getSlotIcon } from "./hotbar";

export type InventoryScreen = {
  show: () => void;
  /** Open directly on the crafting tab (right-click on a crafting table). */
  showCraftTab: () => void;
  hide: () => void;
  toggle: () => void;
  isVisible: () => boolean;
  /** Recipe detail layering — ESC closes the detail first, then the screen. */
  isDetailOpen: () => boolean;
  closeDetail: () => void;
  dispose: () => void;
};

type Category = { id: string; label: string; recipes: Recipe[] };

function itemName(id: number): string {
  return BLOCK_NAMES[id] || `Item ${id}`;
}

function categorize(): Category[] {
  const basic: Recipe[] = [];
  const tools: Recipe[] = [];
  const food: Recipe[] = [];
  const smelting: Recipe[] = [];
  for (const recipe of RECIPES) {
    if (recipe.requiresFurnace) smelting.push(recipe);
    else if (recipe.requiresTable) tools.push(recipe);
    else if (isFood(recipe.output.id)) food.push(recipe);
    else basic.push(recipe);
  }
  return [
    { id: "basic", label: "Básico", recipes: basic },
    { id: "tools", label: "Ferramentas 🔨", recipes: tools },
    { id: "smelting", label: "Fundição 🔥", recipes: smelting },
    { id: "food", label: "Comida", recipes: food },
  ];
}

/* Game-style UI (Minecraft-inspired): chunky bevel borders, flat dark
   panels, pixel font sizing, no web-page look. */
const STYLE = `
#inventory-screen { position: fixed; inset: 0; background: rgba(10,10,18,0.62); z-index: 140; display: none; font-family: 'Segoe UI', system-ui, sans-serif; color: #e0e0e0; }
.cg-panel { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(680px, 94vw); height: min(480px, 86vh); background: #2b2b33; border: 3px solid; border-color: #6e6e78 #1c1c22 #1c1c22 #6e6e78; box-shadow: 0 0 0 3px #0008, 0 10px 40px #000a; padding: 8px 10px; display: flex; flex-direction: column; image-rendering: pixelated; }
.cg-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.cg-title { font-size: 0.95rem; font-weight: 800; color: #ffd766; text-shadow: 1px 1px 0 #000; letter-spacing: 0.5px; }
.cg-close { width: 34px; height: 34px; border: 2px solid; border-color: #6e6e78 #1c1c22 #1c1c22 #6e6e78; background: #3a3a44; color: #ddd; font-size: 0.95rem; cursor: pointer; font-weight: 800; }
.cg-close:active { border-color: #1c1c22 #6e6e78 #6e6e78 #1c1c22; }
.cg-tabs { display: flex; gap: 3px; margin-bottom: 6px; }
.cg-tab { padding: 6px 12px; border: 2px solid; border-color: #6e6e78 #1c1c22 #1c1c22 #6e6e78; background: #3a3a44; color: #aaa; cursor: pointer; font-size: 0.75rem; font-weight: 700; text-shadow: 1px 1px 0 #000; }
.cg-tab.active { background: #1f3324; color: #7dff9a; border-color: #1c1c22 #6e6e78 #6e6e78 #1c1c22; }
.cg-badge { display: inline-block; min-width: 16px; padding: 0 4px; margin-left: 5px; background: #4ade80; color: #06230f; font-size: 0.65rem; font-weight: 800; border: 1px solid #06230f; }
.cg-body { overflow: hidden; flex: 1; display: flex; flex-direction: column; }
.cg-craft { display: grid; grid-template-columns: 1fr 250px; gap: 8px; flex: 1; min-height: 0; }
.cg-list { overflow-y: auto; padding-right: 4px; }
.cg-list::-webkit-scrollbar { width: 8px; }
.cg-list::-webkit-scrollbar-thumb { background: #55555f; border: 2px solid #2b2b33; }
.cg-cat-title { font-size: 0.68rem; font-weight: 800; color: #ffd766; text-shadow: 1px 1px 0 #000; text-transform: uppercase; letter-spacing: 1px; margin: 6px 0 4px; }
.cg-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 3px; }
.cg-card { display: flex; align-items: center; gap: 6px; min-height: 40px; padding: 4px 6px; border: 2px solid; border-color: #55555f #17171d #17171d #55555f; background: #3a3a44; color: #eee; cursor: pointer; text-align: left; font-size: 0.72rem; font-weight: 600; }
.cg-card:hover { background: #44444f; }
.cg-card:active, .cg-card.selected { border-color: #17171d #55555f #55555f #17171d; background: #1f3324; }
.cg-card.blocked { opacity: 0.5; }
.cg-card .icon { width: 26px; height: 26px; image-rendering: pixelated; flex-shrink: 0; }
.cg-card .state { margin-left: auto; font-weight: 800; }
.cg-detail { border: 2px solid; border-color: #17171d #55555f #55555f #17171d; background: #232329; padding: 10px; overflow-y: auto; }
.cg-detail-empty { color: #777; font-size: 0.75rem; text-align: center; padding: 30px 8px; }
.cg-ing { display: flex; align-items: center; gap: 6px; padding: 4px 0; font-size: 0.75rem; }
.cg-ing .icon { width: 22px; height: 22px; image-rendering: pixelated; }
.cg-ing .have { margin-left: auto; font-weight: 800; }
.cg-banner { margin: 8px 0; padding: 6px 8px; font-size: 0.7rem; line-height: 1.4; border: 2px solid; }
.cg-banner.warn { background: #3a2f10; border-color: #17110a #b8860b #b8860b #17110a; color: #ffd766; }
.cg-banner.err { background: #3a1515; border-color: #170a0a #a33 #a33 #170a0a; color: #ff9c9c; }
.cg-craft-btn { width: 100%; min-height: 42px; border: 2px solid; border-color: #7dff9a #14401f #14401f #7dff9a; background: #2e7d32; color: #fff; font-size: 0.9rem; font-weight: 800; cursor: pointer; letter-spacing: 1px; text-shadow: 1px 1px 0 #000; }
.cg-craft-btn:active { border-color: #14401f #7dff9a #7dff9a #14401f; }
.cg-craft-btn:disabled { background: #33333d; color: #666; border-color: #44444e #22222a #22222a #44444e; cursor: default; text-shadow: none; }
.cg-craft-all { width: 100%; min-height: 34px; margin-top: 6px; border: 2px solid; border-color: #55555f #17171d #17171d #55555f; background: #3a3a44; color: #7dff9a; font-size: 0.72rem; font-weight: 800; cursor: pointer; }
.cg-craft-all:disabled { color: #555; cursor: default; }
.cg-grid { display: grid; grid-template-columns: repeat(9, 44px); gap: 3px; justify-content: center; padding-top: 8px; }
.cg-slot { width: 44px; height: 44px; background: #232329; border: 2px solid; border-color: #17171d #55555f #55555f #17171d; display: flex; align-items: center; justify-content: center; position: relative; }
.cg-slot.hotbar { border-color: #17171d #8a8a96 #8a8a96 #17171d; }
.cg-footer { text-align: center; margin-top: 5px; font-size: 0.62rem; color: #777; text-shadow: 1px 1px 0 #000; }
.cg-toast { position: absolute; left: 50%; transform: translateX(-50%); background: #14301a; border: 2px solid #4ade80; color: #7dff9a; padding: 3px 10px; font-size: 0.75rem; font-weight: 800; pointer-events: none; transition: opacity 0.6s, top 0.6s; }
@media (max-width: 720px) {
  .cg-panel { width: 96vw; height: 90vh; }
  .cg-craft { grid-template-columns: 1fr; }
  .cg-cards { grid-template-columns: 1fr; }
  .cg-detail { position: fixed; left: 6px; right: 6px; bottom: 6px; top: auto; z-index: 160; box-shadow: 0 -6px 30px #000c; max-height: 55vh; }
  .cg-grid { grid-template-columns: repeat(9, minmax(30px, 1fr)); }
}
`;

export function createInventoryScreen(
  inventory: Inventory,
  classDef: ClassDef,
  callbacks: {
    onShow: () => void;
    onHide: () => void;
    /** Crafts ONE batch of the recipe. Returns success. */
    onCraft?: (recipe: Recipe) => boolean;
    isNearTable?: () => boolean;
    isNearFurnace?: () => boolean;
    /** Inventory contents changed (moved a stack) — refresh the hotbar. */
    onChanged?: () => void;
    /** Currently selected hotbar slot (0-8) and a setter to equip a slot. */
    getSelectedHotbar?: () => number;
    setSelectedHotbar?: (index: number) => void;
  }
): InventoryScreen {
  type TabId = "craft" | "inv" | "char";
  let visible = false;
  let activeTab: TabId = "craft";
  let selectedRecipe: Recipe | null = null;
  let detailOpen = false; // only meaningful on narrow screens (bottom sheet)

  const style = document.createElement("style");
  style.textContent = STYLE;
  document.head.appendChild(style);

  const overlay = document.createElement("div");
  overlay.id = "inventory-screen";
  overlay.addEventListener("pointerdown", (e) => {
    if (e.target === overlay) hide(); // backdrop tap closes
  });

  const panel = document.createElement("div");
  panel.className = "cg-panel";
  overlay.appendChild(panel);

  // ─── Header ───
  const header = document.createElement("div");
  header.className = "cg-header";
  const title = document.createElement("div");
  title.className = "cg-title";
  title.textContent = "Inventário & Criação";
  const closeButton = document.createElement("button");
  closeButton.className = "cg-close";
  closeButton.textContent = "✕";
  closeButton.setAttribute("aria-label", "Fechar");
  closeButton.addEventListener("click", () => hide());
  header.append(title, closeButton);
  panel.appendChild(header);

  // ─── Tabs ───
  const tabsBar = document.createElement("div");
  tabsBar.className = "cg-tabs";
  panel.appendChild(tabsBar);

  const body = document.createElement("div");
  body.className = "cg-body";
  panel.appendChild(body);

  const footer = document.createElement("div");
  footer.className = "cg-footer";
  footer.textContent = "E ou ESC fecham · clique numa receita para ver os detalhes";
  panel.appendChild(footer);

  const tabDefs: Array<{ id: TabId; label: string }> = [
    { id: "craft", label: "Criação" },
    { id: "inv", label: "Inventário" },
    { id: "char", label: "Personagem" },
  ];
  const tabButtons = new Map<string, HTMLButtonElement>();
  for (const tab of tabDefs) {
    const button = document.createElement("button");
    button.className = "cg-tab";
    button.setAttribute("role", "tab");
    button.addEventListener("click", () => {
      activeTab = tab.id;
      render();
    });
    tabsBar.appendChild(button);
    tabButtons.set(tab.id, button);
  }

  function craftableCount(): number {
    const nearTable = callbacks.isNearTable?.() ?? true;
    const nearFurnace = callbacks.isNearFurnace?.() ?? true;
    return RECIPES.filter(
      (r) => canCraft(inventory, r, nearTable, nearFurnace) && inventory.canFit(r.output.id, r.output.count)
    ).length;
  }

  function refreshTabs() {
    for (const tab of tabDefs) {
      const button = tabButtons.get(tab.id)!;
      const isActive = activeTab === tab.id;
      button.className = `cg-tab${isActive ? " active" : ""}`;
      button.innerHTML = tab.label;
      if (tab.id === "craft") {
        const n = craftableCount();
        if (n > 0) button.innerHTML += `<span class="cg-badge">${n}</span>`;
      }
    }
  }

  // ─── Craft tab ───
  function maxCraftTimes(recipe: Recipe): number {
    let times = Infinity;
    for (const ing of recipe.inputs) {
      times = Math.min(times, Math.floor(inventory.getCount(ing.id) / ing.count));
    }
    return Number.isFinite(times) ? times : 0;
  }

  function recipeStatus(recipe: Recipe): { ok: boolean; reason: "ok" | "table" | "furnace" | "full" | "missing" } {
    const nearTable = callbacks.isNearTable?.() ?? true;
    const nearFurnace = callbacks.isNearFurnace?.() ?? true;
    if (recipe.requiresTable && !nearTable) return { ok: false, reason: "table" };
    if (recipe.requiresFurnace && !nearFurnace) return { ok: false, reason: "furnace" };
    if (!canCraft(inventory, recipe, true, true)) return { ok: false, reason: "missing" };
    if (!inventory.canFit(recipe.output.id, recipe.output.count)) return { ok: false, reason: "full" };
    return { ok: true, reason: "ok" };
  }

  function renderCraftTab(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "cg-craft";

    const list = document.createElement("div");
    list.className = "cg-list";
    for (const category of categorize()) {
      const catTitle = document.createElement("div");
      catTitle.className = "cg-cat-title";
      catTitle.textContent = category.label;
      list.appendChild(catTitle);

      const cardsGrid = document.createElement("div");
      cardsGrid.className = "cg-cards";
      list.appendChild(cardsGrid);

      // Craftable first, blocked last — never hidden
      const sorted = [...category.recipes].sort(
        (a, b) => Number(recipeStatus(b).ok) - Number(recipeStatus(a).ok)
      );
      for (const recipe of sorted) {
        const status = recipeStatus(recipe);
        const card = document.createElement("button");
        card.className = `cg-card${selectedRecipe?.id === recipe.id ? " selected" : ""}${status.ok ? "" : " blocked"}`;
        card.setAttribute("role", "button");
        card.setAttribute("aria-label", `Receita: ${recipe.name}`);
        card.innerHTML = `
          <img class="icon" src="${getSlotIcon(recipe.output.id, 26)}" alt="" />
          <span>${recipe.name}${recipe.output.count > 1 ? ` ×${recipe.output.count}` : ""}</span>
          <span class="state" style="color:${status.ok ? "#7dff9a" : "#777"}">${status.ok ? "✔" : "✖"}</span>
        `;
        card.addEventListener("click", () => {
          selectedRecipe = recipe;
          detailOpen = true;
          render();
        });
        cardsGrid.appendChild(card);
      }
    }
    wrap.appendChild(list);
    wrap.appendChild(renderDetail());
    return wrap;
  }

  function renderDetail(): HTMLElement {
    const detail = document.createElement("div");
    detail.className = "cg-detail";
    if (window.innerWidth <= 720 && !detailOpen) {
      detail.style.display = "none";
      return detail;
    }

    const recipe = selectedRecipe;
    if (!recipe) {
      detail.innerHTML = `<div class="cg-detail-empty">Escolha uma receita na lista<br>para ver os ingredientes</div>`;
      return detail;
    }

    const status = recipeStatus(recipe);
    const times = maxCraftTimes(recipe);

    const head = document.createElement("div");
    head.style.cssText = "display:flex;align-items:center;gap:10px;margin-bottom:8px;";
    head.innerHTML = `
      <img class="icon" style="width:48px;height:48px;image-rendering:pixelated;border-radius:6px;" src="${getSlotIcon(recipe.output.id, 48)}" alt="" />
      <div>
        <div style="font-weight:800;color:#fff;">${recipe.name}</div>
        <div style="font-size:0.75rem;color:#999;">produz ${recipe.output.count}</div>
      </div>
    `;
    detail.appendChild(head);

    const needTitle = document.createElement("div");
    needTitle.className = "cg-cat-title";
    needTitle.textContent = "Precisa de";
    detail.appendChild(needTitle);

    for (const ing of recipe.inputs) {
      const have = inventory.getCount(ing.id);
      const enough = have >= ing.count;
      const row = document.createElement("div");
      row.className = "cg-ing";
      row.innerHTML = `
        <img class="icon" src="${getSlotIcon(ing.id, 26)}" alt="" />
        <span>${itemName(ing.id)}</span>
        <span class="have" style="color:${enough ? "#4ade80" : "#f87171"}">
          ${have}/${ing.count}${enough ? " ✔" : ` (faltam ${ing.count - have})`}
        </span>
      `;
      detail.appendChild(row);
    }

    // Blocked-state banner in plain language
    if (status.reason === "table") {
      const banner = document.createElement("div");
      banner.className = "cg-banner warn";
      banner.textContent = "🔨 Chegue perto de uma Mesa de trabalho para criar este item";
      detail.appendChild(banner);
      const link = document.createElement("button");
      link.className = "cg-craft-all";
      link.textContent = "Ver receita da mesa ›";
      link.addEventListener("click", () => {
        selectedRecipe = RECIPES.find((r) => r.id === "crafting_table") ?? null;
        render();
      });
      detail.appendChild(link);
    } else if (status.reason === "furnace") {
      const banner = document.createElement("div");
      banner.className = "cg-banner warn";
      banner.textContent = "🔥 Chegue perto de uma Fornalha para fundir/cozinhar (precisa de 1 Carvão)";
      detail.appendChild(banner);
      const link = document.createElement("button");
      link.className = "cg-craft-all";
      link.textContent = "Ver receita da fornalha ›";
      link.addEventListener("click", () => {
        selectedRecipe = RECIPES.find((r) => r.id === "furnace") ?? null;
        render();
      });
      detail.appendChild(link);
    } else if (status.reason === "full") {
      const banner = document.createElement("div");
      banner.className = "cg-banner err";
      banner.textContent = "Inventário cheio — use ou solte algo primeiro";
      detail.appendChild(banner);
    }

    const craftButton = document.createElement("button");
    craftButton.className = "cg-craft-btn";
    craftButton.textContent = "CRAFTAR";
    craftButton.disabled = !status.ok;
    craftButton.addEventListener("click", () => doCraft(recipe, 1, craftButton));
    detail.appendChild(craftButton);

    const craftAll = document.createElement("button");
    craftAll.className = "cg-craft-all";
    craftAll.textContent = `Craftar tudo (×${times})`;
    craftAll.disabled = !status.ok || times < 2;
    craftAll.addEventListener("click", () => doCraft(recipe, times, craftButton));
    detail.appendChild(craftAll);

    if (window.innerWidth <= 720) {
      const back = document.createElement("button");
      back.className = "cg-craft-all";
      back.style.marginTop = "6px";
      back.textContent = "‹ Voltar";
      back.addEventListener("click", () => closeDetail());
      detail.appendChild(back);
    }

    return detail;
  }

  function doCraft(recipe: Recipe, times: number, anchor: HTMLElement) {
    let made = 0;
    for (let i = 0; i < times; i++) {
      if (!callbacks.onCraft?.(recipe)) break;
      made++;
    }
    if (made > 0) {
      showToast(`+${made * recipe.output.count} ${recipe.name}`, anchor);
    }
    render(); // counters + button states update, detail stays open
  }

  function showToast(text: string, anchor: HTMLElement) {
    const toast = document.createElement("div");
    toast.className = "cg-toast";
    toast.textContent = text;
    const rect = anchor.getBoundingClientRect();
    toast.style.position = "fixed";
    toast.style.left = `${rect.left + rect.width / 2}px`;
    toast.style.top = `${rect.top - 10}px`;
    document.body.appendChild(toast);
    requestAnimationFrame(() => {
      toast.style.top = `${rect.top - 40}px`;
      toast.style.opacity = "0";
    });
    setTimeout(() => toast.remove(), 700);
  }

  // ─── Inventory tab — ONE click moves an item: a backpack item goes
  //     straight to the hotbar (and into the hand); a hotbar item goes
  //     back to the backpack. No cursor state to confuse anyone. ───
  const INV_MAX_STACK = 64;

  function firstEmpty(from: number, to: number): number {
    for (let j = from; j < to; j++) if (!inventory.items[j]) return j;
    return -1;
  }

  function moveSlot(i: number) {
    const item = inventory.items[i];
    if (!item || item.count <= 0) return;

    if (i < 9) {
      // Hand/hotbar → backpack
      const target = firstEmpty(9, inventory.items.length);
      if (target !== -1) {
        inventory.items[target] = item;
        inventory.items[i] = null;
      }
    } else {
      // Backpack → hotbar (the hand): merge into a same-item hotbar
      // stack, else an empty hotbar slot, else swap the selected slot.
      let landed = -1;
      for (let j = 0; j < 9 && item.count > 0; j++) {
        const h = inventory.items[j];
        if (h && h.blockId === item.blockId && h.count < INV_MAX_STACK) {
          const add = Math.min(item.count, INV_MAX_STACK - h.count);
          h.count += add;
          item.count -= add;
          landed = j;
        }
      }
      if (item.count > 0) {
        let target = firstEmpty(0, 9);
        if (target === -1) target = callbacks.getSelectedHotbar?.() ?? 0;
        const displaced = inventory.items[target];
        inventory.items[target] = item;
        inventory.items[i] = displaced; // swap (null if the slot was empty)
        landed = target;
      } else {
        inventory.items[i] = null; // fully merged into the hotbar
      }
      if (landed >= 0) callbacks.setSelectedHotbar?.(landed); // put it in hand
    }

    callbacks.onChanged?.();
    render();
  }

  function renderInvTab(): HTMLElement {
    const wrap = document.createElement("div");

    const helpBar = document.createElement("div");
    helpBar.className = "cg-cat-title";
    helpBar.textContent =
      "Clique num item da MOCHILA para mandá-lo à hotbar (mão). Clique num item da HOTBAR (linha de cima) para guardar.";
    wrap.appendChild(helpBar);

    const grid = document.createElement("div");
    grid.className = "cg-grid";
    for (let i = 0; i < inventory.items.length; i++) {
      const item = inventory.items[i];
      const slot = document.createElement("button");
      // First 9 = hotbar row (brighter border), rest = backpack
      slot.className = `cg-slot${i < 9 ? " hotbar" : ""}`;
      slot.style.cursor = "pointer";
      slot.style.padding = "0";
      if (item && item.count > 0) {
        slot.innerHTML = `
          <img style="width:30px;height:30px;image-rendering:pixelated;pointer-events:none;" src="${getSlotIcon(item.blockId, 30)}" alt="${itemName(item.blockId)}" title="${itemName(item.blockId)}" />
          ${item.count > 1 ? `<span style="position:absolute;bottom:1px;right:3px;font-size:10px;font-weight:bold;font-family:monospace;text-shadow:0 1px 2px #000;pointer-events:none;">${item.count}</span>` : ""}
        `;
      }
      const index = i;
      slot.addEventListener("click", () => moveSlot(index));
      grid.appendChild(slot);
    }
    wrap.appendChild(grid);

    const hint = document.createElement("div");
    hint.className = "cg-footer";
    hint.textContent =
      "Linha de cima = hotbar (o que você usa na mão). Espaço cheio? Crafte um Baú (8 tábuas).";
    wrap.appendChild(hint);
    return wrap;
  }

  // ─── Character tab ───
  function renderCharTab(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.innerHTML = `
      <div style="padding:14px;background:#1a1a2e;border-radius:8px;border:1px solid #333;margin-bottom:12px;">
        <div style="font-size:1.2rem;font-weight:700;color:${classDef.color};">${classDef.name}</div>
        <div style="font-size:0.8rem;color:#888;font-style:italic;margin-bottom:6px;">${classDef.title}</div>
        <div style="font-size:0.75rem;color:#666;">HP: ${classDef.maxHealth} · MP: ${classDef.maxMana} · Regen: ${classDef.manaRegen}/s</div>
      </div>
    `;
    for (let i = 0; i < classDef.abilities.length; i++) {
      const ability = classDef.abilities[i];
      const card = document.createElement("div");
      card.style.cssText = `padding:10px 12px;background:#1a1a2e;border:1px solid ${ability.color}33;border-left:3px solid ${ability.color};border-radius:6px;margin-bottom:8px;`;
      const stats = [`Mana: ${ability.manaCost}`, `CD: ${ability.cooldown}s`];
      if (ability.damage > 0) stats.push(`Dano: ${ability.damage}`);
      if (ability.duration > 0) stats.push(`Duração: ${ability.duration}s`);
      card.innerHTML = `
        <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
          <span style="font-weight:600;color:${ability.color};font-size:0.9rem;">${ability.name}</span>
          <span style="background:#333;color:#ccc;font-size:10px;font-family:monospace;padding:2px 6px;border-radius:3px;font-weight:bold;">${i + 1}</span>
        </div>
        <div style="font-size:0.75rem;color:#999;">${ability.description}</div>
        <div style="font-size:0.65rem;color:#666;margin-top:4px;">${stats.join(" · ")}</div>
      `;
      wrap.appendChild(card);
    }
    return wrap;
  }

  function render() {
    refreshTabs();
    body.innerHTML = "";
    if (activeTab === "craft") body.appendChild(renderCraftTab());
    else if (activeTab === "inv") body.appendChild(renderInvTab());
    else body.appendChild(renderCharTab());
  }

  document.body.appendChild(overlay);

  function show() {
    visible = true;
    render();
    overlay.style.display = "block";
    callbacks.onShow();
  }

  function showCraftTab() {
    activeTab = "craft";
    show();
  }

  function hide() {
    visible = false;
    detailOpen = false;
    overlay.style.display = "none";
    callbacks.onHide();
  }

  function toggle() {
    if (visible) hide();
    else show();
  }

  function closeDetail() {
    detailOpen = false;
    selectedRecipe = null;
    render();
  }

  return {
    show,
    showCraftTab,
    hide,
    toggle,
    isVisible: () => visible,
    isDetailOpen: () => visible && detailOpen && window.innerWidth <= 720,
    closeDetail,
    dispose() {
      overlay.remove();
      style.remove();
    },
  };
}
